/**
 * Getalbegrip (GET) Support Registry
 *
 * Support content (hints, explanations, visuals) for GET families.
 *
 * Module: GET (Getalbegrip)
 * Families: 50 (100% coverage - largest module!)
 * Coverage: 100%
 *
 * Created: 2026-03-25
 * Status: ✅ Content migrated from familySupportRegistry.ts (Step 4)
 *
 * Skills covered:
 * - positiestelsel (6 families)
 * - breuken (13 families)
 * - kommagetallen (10 families)
 * - procenten (8 families)
 * - verhoudingen (4 families)
 * - negatieve_getallen (3 families)
 * - machten_wortels (3 families)
 * - wetenschappelijk (2 families)
 */

import { FamilySupportEntry } from '../familySupportRegistry'

/**
 * GET Support Definitions
 *
 * Each entry provides:
 * - defaultExplanation: Student-facing "Zo werkt het" text
 * - hints: Progressive hint steps (title + content)
 * - requiresHints: Whether hints are conceptually needed
 * - requiresVisual: Whether visual aid is conceptually needed
 * - defaultVisual: Optional default visual aid reference
 */
export const GET_SUPPORT: Record<string, FamilySupportEntry> = {
  // ══════════════════════════════════════════════════════════
  // Skill: positiestelsel (6 families)
  // ══════════════════════════════════════════════════════════
  'GET-GROOT-POS': {
    familyId: 'GET-GROOT-POS',
    defaultExplanation:
      'In ons talstelsel heeft elk cijfer een positiewaarde. In 78.432: de 7 staat op de tienduizendtallen (70.000), de 8 op de duizendtallen (8.000), enzovoort.',
    defaultVisual: {
      type: 'numberline',
      data: { start: 0, end: 100000, highlight: [78432] },
      showWhen: 'wrong',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        content:
          'Kijk goed naar de plek van het cijfer:\nTD - D - H - T - E\n(Tienduizendtallen - Duizendtallen - Honderdtallen - Tientallen - Eenheden)',
      },
      {
        title: 'Voorbeeld: 78.432',
        content:
          '7 = 70.000 (tienduizendtallen)\n8 = 8.000 (duizendtallen)\n4 = 400 (honderdtallen)\n3 = 30 (tientallen)\n2 = 2 (eenheden)',
      },
    ],
  },
  'GET-GROOT-LEZEN': {
    familyId: 'GET-GROOT-LEZEN',
    defaultExplanation:
      'Grote getallen lezen en schrijven: splits het getal in groepjes van drie cijfers van rechts naar links. Gebruik punten als scheidingsteken.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Groepjes van drie',
        content:
          'Lees van links naar rechts:\n1.234.567 = één miljoen tweehonderdvierendertigduizend vijfhonderdzevenenzestig\n\nSplits: 1 | 234 | 567\nmiljoenen | duizendtallen | eenheden',
      },
    ],
  },
  'GET-GROOT-VERGELIJK': {
    familyId: 'GET-GROOT-VERGELIJK',
    defaultExplanation:
      'Grote getallen vergelijken: kijk eerst naar het aantal cijfers. Bij gelijk aantal cijfers vergelijk je van links naar rechts.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Stap 1: Tel het aantal cijfers (meer cijfers = groter)\nStap 2: Gelijk aantal? Vergelijk van links naar rechts\nStap 3: Het eerste verschil bepaalt welk getal groter is\n\n78.432 vs 78.512 → 4 < 5 → 78.432 < 78.512',
      },
    ],
  },
  'GET-GROOT-AFRONDEN': {
    familyId: 'GET-GROOT-AFRONDEN',
    defaultExplanation:
      'Afronden op tientallen of honderdtallen: kijk naar het cijfer rechts van de positie waarop je afrondt.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Afrondingsregel',
        content:
          'Kijk naar het cijfer rechts van de afrondpositie:\n5 of hoger → omhoog\nLager dan 5 → omlaag\n\n347 op tientallen: kijk naar 7 → omhoog → 350\n342 op tientallen: kijk naar 2 → omlaag → 340',
      },
    ],
  },
  'GET-ROND-BASIS': {
    familyId: 'GET-ROND-BASIS',
    defaultExplanation:
      'Afronden op tientallen en honderdtallen: kijk naar het cijfer na de afrondpositie. 5 of meer = omhoog, minder dan 5 = omlaag.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Voorbeelden',
        content:
          'Op tientallen:\n347 → 350 (7 ≥ 5)\n342 → 340 (2 < 5)\n\nOp honderdtallen:\n347 → 300 (4 < 5)\n362 → 400 (6 ≥ 5)',
      },
    ],
  },
  'GET-GROOT-SCHATTEN': {
    familyId: 'GET-GROOT-SCHATTEN',
    defaultExplanation:
      'Schatten: rond de getallen af voordat je rekent. Zo krijg je snel een schatting van het antwoord.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Strategie',
        content:
          'Rond af op handige getallen:\n298 + 512 ≈ 300 + 500 = 800\n49 × 21 ≈ 50 × 20 = 1.000\n\nDe schatting hoeft niet exact te zijn!',
      },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // Skill: breuken (13 families)
  // ══════════════════════════════════════════════════════════
  'GET-BREUK-BASIS': {
    familyId: 'GET-BREUK-BASIS',
    defaultExplanation:
      'Een breuk is een deel van een geheel. De teller (boven) is hoeveel delen je hebt, de noemer (onder) is het totaal aantal delen.',
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Teller en noemer',
        content:
          'Teller (boven): hoeveel delen je hebt\nNoemer (onder): hoeveel delen het geheel heeft\n\n3/4 = 3 van de 4 delen\n1/2 = 1 van de 2 delen = de helft',
      },
    ],
  },
  'GET-BREUK-LEZEN': {
    familyId: 'GET-BREUK-LEZEN',
    defaultExplanation:
      'Breuken lezen: de teller (boven) en noemer (onder). 3/4 lees je als "drie vierde".',
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Lezen',
        content:
          '1/2 = een half\n1/3 = een derde\n1/4 = een kwart\n2/3 = twee derde\n3/4 = drie kwart\n5/8 = vijf achtste',
      },
    ],
  },
  'GET-BREUK-GEL-PLUS': {
    familyId: 'GET-BREUK-GEL-PLUS',
    defaultExplanation:
      'Bij gelijknamige breuken (zelfde noemer) tel je alleen de tellers op. De noemer blijft gelijk. Voorbeeld: 1/4 + 2/4 = 3/4.',
    defaultVisual: {
      type: 'fraction',
      data: { numerator: 3, denominator: 4, label: '3/4', usePizza: true },
      showWhen: 'always',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Teller en Noemer',
        content:
          'De **teller** (boven) is hoeveel stukken je hebt.\nDe **noemer** (onder) is het totaal aantal stukken.',
        visual: {
          type: 'fraction',
          data: {
            numerator: 3,
            denominator: 4,
            label: 'Driekwart (3/4)',
            visualType: 'pizza',
            usePizza: true,
          },
        },
      },
      {
        title: 'Breuken Optellen',
        content:
          'Bij gelijke noemers: tel alleen de tellers op! De noemer blijft gelijk.\nVoorbeeld: 1/4 + 2/4 = 3/4',
      },
    ],
  },
  'GET-BREUK-GEL-MIN': {
    familyId: 'GET-BREUK-GEL-MIN',
    defaultExplanation:
      'Bij gelijknamige breuken aftrekken: trek alleen de tellers van elkaar af. De noemer blijft gelijk.',
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Regel',
        content:
          'Zelfde noemer? Trek de tellers af!\n3/4 - 1/4 = 2/4 = 1/2\n5/8 - 2/8 = 3/8\n\nDe noemer verandert NIET.',
      },
    ],
  },
  'GET-BREUK-ONG-PLUS': {
    familyId: 'GET-BREUK-ONG-PLUS',
    defaultExplanation:
      'Bij ongelijknamige breuken moet je eerst de noemers gelijk maken (KGV). Voorbeeld: 1/3 + 1/4 → 4/12 + 3/12 = 7/12.',
    defaultVisual: {
      type: 'fraction',
      data: { numerator: 7, denominator: 12, label: '7/12', usePizza: true },
      showWhen: 'wrong',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Teller en Noemer',
        content:
          'De **teller** (boven) is hoeveel stukken je hebt.\nDe **noemer** (onder) is het totaal aantal stukken.',
      },
      {
        title: 'Breuken Optellen',
        content:
          'Bij gelijke noemers: tel alleen de tellers op! De noemer blijft gelijk.\nVoorbeeld: 1/4 + 2/4 = 3/4',
      },
    ],
  },
  'GET-BREUK-MIN': {
    familyId: 'GET-BREUK-MIN',
    defaultExplanation:
      'Ongelijknamige breuken aftrekken: maak eerst de noemers gelijk (KGV), dan trek je de tellers af.',
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Stap 1: Maak noemers gelijk (KGV)\n1/3 - 1/4 → 4/12 - 3/12\nStap 2: Trek tellers af\n4/12 - 3/12 = 1/12',
      },
    ],
  },
  'GET-BREUK-KEER': {
    familyId: 'GET-BREUK-KEER',
    defaultExplanation:
      'Breuken vermenigvuldigen: teller × teller, noemer × noemer. Voorbeeld: 2/3 × 3/4 = 6/12 = 1/2. Vereenvoudig altijd het antwoord.',
    requiresHints: true,
    requiresVisual: true,
    defaultVisual: {
      type: 'fraction',
      data: {
        numerator: 6,
        denominator: 12,
        label: '6/12 = 1/2',
        usePizza: true,
      },
      showWhen: 'always',
    },
    hints: [
      {
        title: 'Teller en Noemer',
        content:
          'De **teller** (boven) is hoeveel stukken je hebt.\nDe **noemer** (onder) is het totaal aantal stukken.',
      },
    ],
  },
  'GET-BREUK-DEEL': {
    familyId: 'GET-BREUK-DEEL',
    defaultExplanation:
      'Delen door een breuk = vermenigvuldigen met het omgekeerde. Voorbeeld: 3/4 ÷ 2/3 = 3/4 × 3/2 = 9/8 = 1 1/8.',
    requiresHints: true,
    requiresVisual: true,
    defaultVisual: {
      type: 'fraction',
      data: { numerator: 9, denominator: 8, label: '9/8', usePizza: true },
      showWhen: 'always',
    },
    hints: [
      {
        title: 'Teller en Noemer',
        content:
          'De **teller** (boven) is hoeveel stukken je hebt.\nDe **noemer** (onder) is het totaal aantal stukken.',
      },
    ],
  },
  'GET-BREUK-GETALLENLIJN': {
    familyId: 'GET-BREUK-GETALLENLIJN',
    defaultExplanation:
      'Breuken op de getallenlijn: verdeel het stuk tussen 0 en 1 in gelijke delen. De noemer bepaalt hoeveel delen.',
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Aflezen',
        content:
          'Noemer = aantal gelijke delen tussen 0 en 1\nTeller = hoeveel delen je telt\n\n3/4: verdeel in 4 delen, tel 3 → 0,75\n2/5: verdeel in 5 delen, tel 2 → 0,4',
      },
    ],
  },
  'GET-BREUK-VEREENVOUDIG': {
    familyId: 'GET-BREUK-VEREENVOUDIG',
    defaultExplanation:
      'Breuken vereenvoudigen: deel teller en noemer door hetzelfde getal. Zoek de GGD (grootste gemene deler).',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Deel teller EN noemer door hetzelfde getal:\n4/8 → ÷4 → 1/2\n6/9 → ÷3 → 2/3\n\nZoek het grootste getal waardoor beide deelbaar zijn.',
      },
    ],
  },
  'GET-BREUK-UITBREIDEN': {
    familyId: 'GET-BREUK-UITBREIDEN',
    defaultExplanation:
      'Breuken uitbreiden: vermenigvuldig teller en noemer met hetzelfde getal. De waarde van de breuk verandert niet.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Regel',
        content:
          'Vermenigvuldig teller EN noemer met hetzelfde getal:\n1/2 → ×3 → 3/6\n2/3 → ×4 → 8/12\n\nDe breuk wordt niet groter of kleiner!',
      },
    ],
  },
  'GET-BREUK-GEMENGD': {
    familyId: 'GET-BREUK-GEMENGD',
    defaultExplanation:
      'Gemengde getallen bestaan uit een heel getal en een breuk: 2 1/3. Omrekenen: 2 1/3 = 7/3.',
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Omrekenen',
        content:
          'Gemengd → breuk:\n2 1/3 = (2×3+1)/3 = 7/3\n\nBreuk → gemengd:\n7/3 = 2 rest 1 = 2 1/3',
      },
    ],
  },
  'GET-VERGELIJK': {
    familyId: 'GET-VERGELIJK',
    defaultExplanation:
      'Breuken vergelijken: maak de noemers gelijk, dan vergelijk je de tellers. Grotere teller = grotere breuk.',
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Stap 1: Maak de noemers gelijk\n1/3 vs 1/4 → 4/12 vs 3/12\nStap 2: Vergelijk de tellers\n4 > 3 → 1/3 > 1/4',
      },
      {
        title: 'Snelle trucs',
        content:
          'Zelfde teller? Kleinere noemer = groter!\n1/3 > 1/4 (3 < 4, dus 1/3 is groter)\n\nZelfde noemer? Grotere teller = groter!\n3/5 > 2/5',
      },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // Skill: kommagetallen (10 families)
  // ══════════════════════════════════════════════════════════
  'GET-KOMMA-WAARDE': {
    familyId: 'GET-KOMMA-WAARDE',
    defaultExplanation:
      'Bij kommagetallen staan achter de komma de tienden (0,1), honderdsten (0,01) en duizendsten (0,001). Voorbeeld: 3,25 = 3 + 2 tienden + 5 honderdsten.',
    defaultVisual: {
      type: 'numberline',
      data: { start: 0, end: 1, highlight: [0.25, 0.5, 0.75] },
      showWhen: 'wrong',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Positiewaarde achter de komma',
        content:
          'Elk cijfer achter de komma heeft een vaste waarde:\n1e plek = tienden (0,1)\n2e plek = honderdsten (0,01)\n3e plek = duizendsten (0,001)\n\nVoorbeeld: 3,25\n2 = 2 tienden = 0,2\n5 = 5 honderdsten = 0,05',
      },
      {
        title: 'Vergelijken',
        content:
          'Vergelijk cijfer voor cijfer van links naar rechts:\n0,35 vs 0,4 → kijk naar de tienden: 3 < 4 → 0,35 < 0,4\n\nTip: vul aan met nullen: 0,4 = 0,40 → nu is het makkelijker!',
      },
    ],
  },
  'GET-KOMMA-LEZEN': {
    familyId: 'GET-KOMMA-LEZEN',
    defaultExplanation:
      'Kommagetallen lezen: het getal voor de komma zijn de eenheden, na de komma de tienden en honderdsten.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Lezen',
        content:
          '3,5 = drie komma vijf = drie en vijf tienden\n0,25 = nul komma vijfentwintig = vijfentwintig honderdsten\n1,125 = één komma honderdvijfentwintig',
      },
    ],
  },
  'GET-KOMMA-VERGELIJK': {
    familyId: 'GET-KOMMA-VERGELIJK',
    defaultExplanation:
      'Kommagetallen vergelijken: vergelijk cijfer voor cijfer van links naar rechts. Vul aan met nullen als nodig.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Strategie',
        content:
          'Vul aan met nullen:\n0,5 = 0,50\n0,35 vs 0,5 → 0,35 vs 0,50\n3 < 5 → 0,35 < 0,50',
      },
    ],
  },
  'GET-KOMMA-GETALLENLIJN': {
    familyId: 'GET-KOMMA-GETALLENLIJN',
    defaultExplanation:
      'Kommagetallen op de getallenlijn: kijk naar de schaalverdeling. Elke streepje kan 0,1 of 0,01 zijn.',
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Aflezen',
        content:
          'Stap 1: Kijk naar de getallen bij de streepjes\nStap 2: Bereken het verschil per streepje\nStap 3: Tel de streepjes vanaf het dichtstbijzijnde getal',
      },
    ],
  },
  'GET-KOMMA-PLUS': {
    familyId: 'GET-KOMMA-PLUS',
    defaultExplanation:
      'Kommagetallen optellen: zet de komma recht onder elkaar. Tel op zoals gewone getallen.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Strategie',
        content:
          'Zet de komma recht onder elkaar:\n  3,45\n+ 2,30\n------\n  5,75\n\nVul aan met nullen als nodig!',
      },
    ],
  },
  'GET-KOMMA-MIN': {
    familyId: 'GET-KOMMA-MIN',
    defaultExplanation:
      'Kommagetallen aftrekken: zet de komma recht onder elkaar. Trek af zoals gewone getallen.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Strategie',
        content:
          'Zet de komma recht onder elkaar:\n  5,75\n- 2,30\n------\n  3,45\n\nVul aan met nullen als nodig!',
      },
    ],
  },
  'GET-KOMMA-KEER': {
    familyId: 'GET-KOMMA-KEER',
    defaultExplanation:
      'Kommagetallen vermenigvuldigen: reken eerst zonder komma, tel dan het totaal aantal decimalen.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Stap 1: Reken zonder komma\n2,5 × 1,2 → 25 × 12 = 300\nStap 2: Tel decimalen (1+1=2)\nStap 3: Zet de komma: 3,00 = 3',
      },
    ],
  },
  'GET-KOMMA-DEEL': {
    familyId: 'GET-KOMMA-DEEL',
    defaultExplanation:
      'Kommagetallen delen: maak de deler een heel getal door beide getallen met 10/100 te vermenigvuldigen.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Strategie',
        content:
          'Maak de deler heel:\n4,5 ÷ 1,5 → 45 ÷ 15 = 3\n(beide ×10)\n\n6,3 ÷ 0,7 → 63 ÷ 7 = 9',
      },
    ],
  },
  'GET-DEEL-KOMMA': {
    familyId: 'GET-DEEL-KOMMA',
    defaultExplanation:
      'Delen door een kommagetal: vermenigvuldig deler en deeltal met 10 of 100 zodat de deler een heel getal wordt.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          '12 ÷ 0,4 → 120 ÷ 4 = 30\n(beide ×10)\n\n5 ÷ 0,25 → 500 ÷ 25 = 20\n(beide ×100)',
      },
    ],
  },
  'GET-KOMMA-AFRONDEN': {
    familyId: 'GET-KOMMA-AFRONDEN',
    defaultExplanation:
      'Afronden op decimalen: kijk naar het cijfer na de gevraagde positie. Is het 5 of hoger? Rond omhoog af. Lager dan 5? Rond omlaag af.',
    defaultVisual: {
      type: 'numberline',
      data: {
        start: 3.4,
        end: 3.5,
        marked: [3.47],
        step: 0.1,
        label: '3,47 op de getallenlijn',
      },
      showWhen: 'wrong',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Afrondingsregel',
        content:
          'Kijk naar het cijfer NA de positie waarop je afrondt.\n5 of hoger → omhoog\nLager dan 5 → omlaag\n\nVoorbeeld: 3,47 afronden op 1 decimaal\nKijk naar de 7 (honderdsten). 7 ≥ 5 → omhoog: 3,5',
      },
      {
        title: 'Doorrol-uitzondering',
        content:
          'Soms loopt het afronden door:\n9,95 op 1 decimaal → 10,0 (niet 9,10!)\nDe 9 op de tiendenplek loopt door naar de eenheden.',
      },
      {
        title: 'Hoeveel decimalen?',
        content:
          'Lees goed hoeveel decimalen gevraagd worden!\n"Op 1 decimaal" = 1 cijfer achter de komma\n"Op 2 decimalen" = 2 cijfers achter de komma\n"Op centen" = 2 decimalen (honderdsten)',
      },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // Skill: procenten (8 families)
  // ══════════════════════════════════════════════════════════
  'GET-PROC-BASIS': {
    familyId: 'GET-PROC-BASIS',
    defaultExplanation:
      'Procent = per honderd. 50% = 50/100 = 0,50 = de helft. 25% = 1/4. 10% = 1/10. Leer deze drieledige koppeling: procent ↔ breuk ↔ decimaal.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          '50% = de helft (÷2)\n25% = een kwart (÷4)\n75% = driekwart\n10% = een tiende (÷10)\n1% = een honderdste (÷100)',
      },
    ],
  },
  'GET-PROC-VAN': {
    familyId: 'GET-PROC-VAN',
    defaultExplanation:
      'Percentage van een bedrag: bereken eerst 1% (deel door 100), vermenigvuldig dan met het gevraagde percentage. Of: bereken 10% (deel door 10) en werk daarmee.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Stap 1: Bereken 10% (deel door 10)\nStap 2: Gebruik 10% om het gevraagde percentage te berekenen\n\nVoorbeeld: 40% van 25\n10% = 2,5 → 40% = 4 × 2,5 = 10',
      },
    ],
  },
  'GET-PROC-NIEUW': {
    familyId: 'GET-PROC-NIEUW',
    defaultExplanation:
      '25% korting? Dan betaal je 75% (100% - 25%). Bereken 75% van de prijs. 15% opslag? Dan betaal je 115%. Vermenigvuldig met 1,15.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          '50% = de helft (÷2)\n25% = een kwart (÷4)\n75% = driekwart\n10% = een tiende (÷10)\n1% = een honderdste (÷100)',
      },
    ],
  },
  'GET-PROC-TERUG': {
    familyId: 'GET-PROC-TERUG',
    defaultExplanation:
      'Terugrekenen: als 30% = €60, dan is 1% = €2, dus 100% = €200. Of: deel het bekende bedrag door het percentage en vermenigvuldig met 100.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          '50% = de helft (÷2)\n25% = een kwart (÷4)\n75% = driekwart\n10% = een tiende (÷10)\n1% = een honderdste (÷100)',
      },
    ],
  },
  'GET-PROC-BTW': {
    familyId: 'GET-PROC-BTW',
    defaultExplanation:
      'BTW exclusief berekenen: prijs inclusief ÷ (1 + BTW%). Bij 21% BTW: €121 inclusief → €121 ÷ 1,21 = €100 exclusief.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          '50% = de helft (÷2)\n25% = een kwart (÷4)\n75% = driekwart\n10% = een tiende (÷10)\n1% = een honderdste (÷100)',
      },
    ],
  },
  'GET-PROC-TOENAME': {
    familyId: 'GET-PROC-TOENAME',
    defaultExplanation:
      'Procentuele toename: nieuw = oud × (1 + percentage/100). 10% toename: × 1,10.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Berekenen',
        content:
          '10% toename: × 1,10\n25% toename: × 1,25\n\nVoorbeeld: €200 + 15% = €200 × 1,15 = €230',
      },
    ],
  },
  'GET-PROC-AFNAME': {
    familyId: 'GET-PROC-AFNAME',
    defaultExplanation:
      'Procentuele afname: nieuw = oud × (1 - percentage/100). 20% afname: × 0,80.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Berekenen',
        content:
          '20% afname: × 0,80\n30% afname: × 0,70\n\nVoorbeeld: €500 - 25% = €500 × 0,75 = €375',
      },
    ],
  },
  'GET-PROC-SAMENGESTELD': {
    familyId: 'GET-PROC-SAMENGESTELD',
    defaultExplanation:
      'Bij samengestelde procentberekeningen pas je meerdere percentages achter elkaar toe. Let op: 20% + 10% ≠ 30%!',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Achter elkaar',
        content:
          'Eerst 20% korting, dan 10% korting:\n€100 × 0,80 = €80\n€80 × 0,90 = €72\n\nDit is NIET hetzelfde als 30% korting (= €70)!',
      },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // Skill: verhoudingen (4 families)
  // ══════════════════════════════════════════════════════════
  'GET-VERH-SCHAAL': {
    familyId: 'GET-VERH-SCHAAL',
    defaultExplanation:
      'Bij een verhoudingstabel zoek je de vermenigvuldigingsfactor. Als 3 → 12 (×4), dan geldt die factor voor de hele rij. Kruislings vermenigvuldigen werkt ook.',
    defaultVisual: {
      type: 'ratio',
      data: {
        headers: ['A', 'B'],
        rows: [
          [3, 12],
          [5, '?'],
        ],
        highlight: [1, 1],
      },
      showWhen: 'wrong',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Stap 1: Tel de delen op (bijv. 2:3 = 5 delen)\nStap 2: Deel het totaal door het aantal delen\nStap 3: Vermenigvuldig met het gewenste deel',
      },
    ],
  },
  'GET-VERH-SCHAAL-KAART': {
    familyId: 'GET-VERH-SCHAAL-KAART',
    defaultExplanation:
      'Schaal op kaarten: 1:100.000 betekent dat 1 cm op de kaart = 100.000 cm = 1 km in werkelijkheid.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Omrekenen',
        content:
          'Schaal 1:100.000\n1 cm op kaart = 100.000 cm = 1.000 m = 1 km\n\n3 cm op kaart = 3 km in het echt\n5 km in het echt = 5 cm op kaart',
      },
    ],
  },
  'GET-VERH-RECEPT': {
    familyId: 'GET-VERH-RECEPT',
    defaultExplanation:
      'Verhoudingen in recepten: als een recept voor 4 personen is en je kookt voor 6, vermenigvuldig je alles met 6/4 = 1,5.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Stap 1: Bereken de factor\nRecept voor 4, je wilt 6: factor = 6 ÷ 4 = 1,5\nStap 2: Vermenigvuldig elk ingrediënt\n200g × 1,5 = 300g\n3 eieren × 1,5 = 4,5 → 5 eieren',
      },
    ],
  },
  'GET-VERH-VERDELEN': {
    familyId: 'GET-VERH-VERDELEN',
    defaultExplanation:
      'Verdelen in verhouding: tel de delen op, deel het totaal door het aantal delen, vermenigvuldig per deel.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Verdeel €120 in verhouding 2:3\nStap 1: Totaal delen = 2+3 = 5\nStap 2: Waarde per deel = €120 ÷ 5 = €24\nStap 3: Deel A = 2 × €24 = €48\nStap 3: Deel B = 3 × €24 = €72',
      },
    ],
  },
  'GET-VERH-BREUK-PROC-KOPPEL': {
    familyId: 'GET-VERH-BREUK-PROC-KOPPEL',
    defaultExplanation:
      'De drieledige koppeling: breuk ↔ decimaal ↔ procent. 1/4 = 0,25 = 25%. Leer de standaardkoppelingen uit je hoofd.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Standaardkoppelingen',
        content:
          '1/2 = 0,5 = 50%\n1/4 = 0,25 = 25%\n3/4 = 0,75 = 75%\n1/5 = 0,2 = 20%\n1/10 = 0,1 = 10%\n1/3 ≈ 0,33 ≈ 33%',
      },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // Skill: negatieve_getallen (3 families)
  // ══════════════════════════════════════════════════════════
  'GET-NEGATIEF': {
    familyId: 'GET-NEGATIEF',
    defaultExplanation:
      'Negatieve getallen staan links van 0 op de getallenlijn. -3 is kleiner dan -1. Hoe verder links, hoe kleiner.',
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Getallenlijn',
        content:
          '... -3  -2  -1  0  1  2  3 ...\n\nLinks = kleiner, rechts = groter\n-5 < -2 < 0 < 3 < 7',
      },
    ],
  },
  'GET-NEGATIEF-PLUS': {
    familyId: 'GET-NEGATIEF-PLUS',
    defaultExplanation:
      'Optellen met negatieve getallen: -3 + 5 = 2 (ga 5 stappen naar rechts). -3 + (-2) = -5 (ga 2 stappen naar links).',
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Regels',
        content:
          'Positief optellen = naar rechts\n-3 + 5 = 2\n\nNegatief optellen = naar links\n-3 + (-2) = -5\n\nTip: gebruik de getallenlijn!',
      },
    ],
  },
  'GET-NEGATIEF-MIN': {
    familyId: 'GET-NEGATIEF-MIN',
    defaultExplanation:
      'Aftrekken met negatieve getallen: min een negatief getal = plus! 5 - (-3) = 5 + 3 = 8.',
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Regels',
        content:
          'Min min = plus!\n5 - (-3) = 5 + 3 = 8\n-2 - (-4) = -2 + 4 = 2\n\nEzelsbruggetje: twee minnetjes worden een plusje.',
      },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // Skill: machten_wortels (3 families)
  // ══════════════════════════════════════════════════════════
  'GET-MACHT': {
    familyId: 'GET-MACHT',
    defaultExplanation:
      'Machtsverheffen: 2³ = 2 × 2 × 2 = 8. Het grondtal (2) vermenigvuldig je zo vaak als de exponent (3) aangeeft.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Uitleg',
        content:
          '2³ = 2 × 2 × 2 = 8\n3² = 3 × 3 = 9\n10⁴ = 10 × 10 × 10 × 10 = 10.000\n\nElk getal tot de macht 0 = 1\nElk getal tot de macht 1 = zichzelf',
      },
    ],
  },
  'GET-MACHT-GRONDTAL': {
    familyId: 'GET-MACHT-GRONDTAL',
    defaultExplanation:
      'Bij machten met grondtal: het grondtal is het getal dat je vermenigvuldigt, de exponent is hoe vaak.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Begrippen',
        content:
          'In 5³:\n5 = grondtal (wat je vermenigvuldigt)\n3 = exponent (hoe vaak)\n125 = uitkomst (5 × 5 × 5)',
      },
    ],
  },
  'GET-WORTEL': {
    familyId: 'GET-WORTEL',
    defaultExplanation:
      'Worteltrekken is het omgekeerde van kwadrateren. √9 = 3 want 3² = 9. √25 = 5 want 5² = 25.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Kwadraten uit je hoofd',
        content:
          '√1 = 1    √4 = 2    √9 = 3\n√16 = 4   √25 = 5   √36 = 6\n√49 = 7   √64 = 8   √81 = 9\n√100 = 10  √121 = 11  √144 = 12',
      },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // Skill: wetenschappelijk (2 families)
  // ══════════════════════════════════════════════════════════
  'GET-WETENSCHAPPELIJK': {
    familyId: 'GET-WETENSCHAPPELIJK',
    defaultExplanation:
      'Wetenschappelijke notatie: een getal als a × 10ⁿ waarbij 1 ≤ a < 10. Voorbeeld: 3.500 = 3,5 × 10³.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Omzetten',
        content:
          'Groot getal → wetenschappelijk:\n3.500 = 3,5 × 10³ (komma 3 plaatsen naar links)\n\nKlein getal → wetenschappelijk:\n0,0042 = 4,2 × 10⁻³ (komma 3 plaatsen naar rechts)',
      },
    ],
  },
  'GET-WETENSCHAP-REKEN': {
    familyId: 'GET-WETENSCHAP-REKEN',
    defaultExplanation:
      'Rekenen met wetenschappelijke notatie: vermenigvuldig de getallen en tel de exponenten op.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Vermenigvuldigen',
        content: '(2 × 10³) × (3 × 10⁴)\n= 2 × 3 × 10³⁺⁴\n= 6 × 10⁷',
      },
    ],
  },
}

// S-5 (Support RC audit 2026): removed unused `GET_SUPPORT_STATS` export.
